import { useState, useEffect } from "react";
import { useNavigate, Link, NavigateFunction, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { BrandLogo, BrandIcon } from "@/components/ui/brand-logo";
import {
  ArrowRight,
  Play,
  CheckCircle2,
  Twitter,
  Linkedin,
  Mail,
  Lock,
  Menu,
  X,
} from "lucide-react";
import HowItWorks from "@/components/landing/HowItWorks";
import ComparisonSection from "@/components/landing/ComparisonSection";
import FeaturesShowcase from "@/components/landing/FeaturesShowcase";
import TestimonialsSection from "@/components/landing/TestimonialsSection";
import PricingPreview from "@/components/landing/PricingPreview";
import AnimatedSection from "@/components/landing/AnimatedSection";
import { PersonaSwitcher } from "@/components/landing/PersonaSwitcher";
import { PainPointsSection } from "@/components/landing/PainPointsSection";
import { WaitlistModal } from "@/components/landing/WaitlistModal";
import { PilotRequestModal } from "@/components/landing/PilotRequestModal";
import { InviteCodeModal } from "@/components/landing/InviteCodeModal";
import { isBetaMode, BETA_CONFIG, PRODUCTION_CONFIG } from "@/lib/launch-mode";
import { useWaitlistCount, formatWaitlistCount } from "@/hooks/useWaitlistCount";
import {
  type Persona,
  personaContent,
  PERSONA_HASH_MAP,
  PERSONA_TO_HASH,
} from "@/lib/persona-content";

// Mobile Menu Component
const MobileMenu = ({
  navigate,
  hasInviteCode,
  onCtaClick
}: {
  navigate: NavigateFunction;
  hasInviteCode: boolean;
  onCtaClick: () => void;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const betaMode = isBetaMode();

  return (
    <div className="md:hidden">
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Toggle menu"
      >
        {isOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </Button>

      {isOpen && (
        <div className="absolute top-full left-0 right-0 bg-background/95 backdrop-blur-xl border-b border-border/50 p-4 space-y-2">
          <Button
            variant="ghost"
            className="w-full justify-start text-muted-foreground hover:text-foreground"
            onClick={() => {
              document.getElementById("features")?.scrollIntoView({ behavior: "smooth" });
              setIsOpen(false);
            }}
          >
            Features
          </Button>
          {!betaMode && (
            <Button
              variant="ghost"
              className="w-full justify-start text-muted-foreground hover:text-foreground"
              onClick={() => {
                navigate("/pricing");
                setIsOpen(false);
              }}
            >
              Pricing
            </Button>
          )}
          <Button
            variant="ghost"
            className="w-full justify-start text-muted-foreground hover:text-foreground"
            onClick={() => {
              navigate("/about");
              setIsOpen(false);
            }}
          >
            About
          </Button>
          <Button
            variant="ghost"
            className="w-full justify-start text-muted-foreground hover:text-foreground"
            onClick={() => {
              navigate("/auth");
              setIsOpen(false);
            }}
          >
            Sign In
          </Button>
          <Button
            className="w-full bg-gradient-to-r from-primary to-accent hover:opacity-90 text-primary-foreground"
            onClick={() => {
              onCtaClick();
              setIsOpen(false);
            }}
          >
            {betaMode
              ? (hasInviteCode ? BETA_CONFIG.primaryCtaWithCode : BETA_CONFIG.primaryCta)
              : PRODUCTION_CONFIG.primaryCta
            }
          </Button>
        </div>
      )}
    </div>
  );
};

const Landing = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showWaitlistModal, setShowWaitlistModal] = useState(false);
  const [showPilotModal, setShowPilotModal] = useState(false);
  const [showInviteCodeModal, setShowInviteCodeModal] = useState(false);
  const betaMode = isBetaMode();
  const hasInviteCode = !!searchParams.get('code');
  const { data: waitlistCount = 977 } = useWaitlistCount();

  // Persona state with URL hash support
  const [persona, setPersona] = useState<Persona>(() => {
    const hash = window.location.hash.replace("#", "");
    return PERSONA_HASH_MAP[hash] || "individual";
  });

  useEffect(() => {
    const onHashChange = () => {
      const hash = window.location.hash.replace("#", "");
      if (PERSONA_HASH_MAP[hash]) {
        setPersona(PERSONA_HASH_MAP[hash]);
      }
    };
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  const handlePersonaChange = (p: Persona) => {
    setPersona(p);
    window.history.replaceState(null, "", `#${PERSONA_TO_HASH[p]}`);
  };

  const content = personaContent[persona];

  // CTA handler — individuals go direct to signup, teams/coaches go to pilot modal
  const handleCta = () => {
    if (betaMode && !hasInviteCode) {
      if (persona === "individual") {
        navigate("/auth");
      } else {
        setShowPilotModal(true);
      }
    } else {
      navigate("/auth");
    }
  };

  // CTA text — in beta, use persona-specific text; otherwise use production config
  const ctaText = betaMode
    ? (hasInviteCode ? BETA_CONFIG.primaryCtaWithCode : content.ctaText)
    : PRODUCTION_CONFIG.primaryCta;

  return (
    <div className="min-h-screen bg-background overflow-hidden">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 border-b border-border/50 bg-background/80 backdrop-blur-xl">
        <div className="container py-4 flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <BrandLogo size="md" />
            {betaMode && (
              <Badge variant="outline" className="text-[10px] px-1.5 py-0.5 border-primary/50 text-primary font-medium">
                Beta
              </Badge>
            )}
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-3">
            <Button
              variant="ghost"
              onClick={() => document.getElementById("features")?.scrollIntoView({ behavior: "smooth" })}
              className="text-muted-foreground hover:text-foreground"
            >
              Features
            </Button>
            {!betaMode && (
              <Button
                variant="ghost"
                onClick={() => navigate("/pricing")}
                className="text-muted-foreground hover:text-foreground"
              >
                Pricing
              </Button>
            )}
            <Button
              variant="ghost"
              onClick={() => navigate("/about")}
              className="text-muted-foreground hover:text-foreground"
            >
              About
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate("/auth")}
              className="text-muted-foreground hover:text-foreground"
            >
              Sign In
            </Button>
            <Button
              onClick={handleCta}
              className="bg-gradient-to-r from-primary to-accent hover:opacity-90 text-primary-foreground"
            >
              {ctaText}
            </Button>
          </div>

          {/* Mobile Menu */}
          <MobileMenu navigate={navigate} hasInviteCode={hasInviteCode} onCtaClick={handleCta} />
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-24 md:pt-44 md:pb-32 bg-gradient-to-br from-[hsl(222,47%,11%)] via-[hsl(240,30%,14%)] to-[hsl(222,47%,11%)]">
        {/* Subtle Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[hsl(277,90%,65%,0.1)] rounded-full blur-[150px]" />
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-[hsl(173,80%,40%,0.1)] rounded-full blur-[120px]" />
        </div>

        <div className="container relative z-10">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            {/* Persona Switcher */}
            <AnimatedSection>
              <PersonaSwitcher persona={persona} onChange={handlePersonaChange} />
            </AnimatedSection>

            {/* Persona-Driven Content — keyed for transition */}
            <div
              key={persona}
              className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-300"
            >
              {/* Main Headline */}
              <AnimatedSection delay={betaMode ? 100 : 50}>
                <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold leading-tight">
                  <span className="text-white">{content.headline}</span>
                  <span className="block mt-3 bg-gradient-to-r from-[hsl(277,90%,65%)] to-[hsl(173,80%,40%)] bg-clip-text text-transparent">
                    {content.headlineGradient}
                  </span>
                </h1>
              </AnimatedSection>

              {/* Subheadline */}
              <AnimatedSection delay={betaMode ? 200 : 100}>
                <div className="text-lg md:text-xl text-muted-foreground max-w-[800px] mx-auto leading-relaxed space-y-1">
                  {content.subheadlines.map((line) => (
                    <p key={line}>{line}</p>
                  ))}
                </div>
              </AnimatedSection>

              {/* CTA Buttons */}
              <AnimatedSection delay={betaMode ? 300 : 200}>
                <div className="flex flex-col sm:flex-row gap-4 justify-center pt-6">
                  <Button
                    size="lg"
                    onClick={handleCta}
                    className="bg-gradient-to-r from-primary to-accent hover:opacity-90 text-white text-lg px-8 py-6 shadow-lg shadow-primary/25"
                  >
                    {ctaText}
                    {betaMode && !hasInviteCode && persona !== "individual" ? <Lock className="ml-2 w-5 h-5" /> : <ArrowRight className="ml-2 w-5 h-5" />}
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    onClick={() => document.getElementById("how-it-works")?.scrollIntoView({ behavior: "smooth" })}
                    className="text-lg px-8 py-6 border-white/20 text-white hover:bg-white/10"
                  >
                    See How It Works
                  </Button>
                </div>
              </AnimatedSection>

              {/* Invite Code Link (Beta Mode, Team/Coach only) */}
              {betaMode && !hasInviteCode && persona !== "individual" && (
                <AnimatedSection delay={350}>
                  <p className="text-sm text-muted-foreground">
                    {BETA_CONFIG.inviteCodePrompt}{' '}
                    <button
                      onClick={() => setShowInviteCodeModal(true)}
                      className="text-primary hover:text-primary/80 underline underline-offset-2 font-medium transition-colors"
                    >
                      {BETA_CONFIG.inviteCodeLink}
                    </button>
                  </p>
                </AnimatedSection>
              )}

              {/* Trust Bar */}
              <AnimatedSection delay={betaMode ? 400 : 300}>
                <p className="text-sm text-muted-foreground pt-4">
                  {betaMode ? (
                    persona === "individual" ? (
                      <>
                        Join <span className="font-semibold text-white">1,000+</span> professionals building careers with intention
                      </>
                    ) : (
                      <>
                        <span className="font-semibold text-white">{formatWaitlistCount(waitlistCount)}</span> professionals already on the waitlist
                      </>
                    )
                  ) : (
                    <>
                      Join <span className="font-semibold text-white">2,000+</span> professionals building careers with
                      intention, not guesswork
                    </>
                  )}
                </p>
              </AnimatedSection>

              {/* Flow Diagram */}
              <AnimatedSection delay={400}>
                <div className="flex flex-wrap items-center justify-center gap-2 md:gap-3 pt-8">
                  {content.flowSteps.map((step, index, arr) => (
                    <div key={step.label} className="flex items-center gap-2 md:gap-3">
                      <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-primary/20 to-accent/20 border border-white/10">
                        <step.icon className="w-3.5 h-3.5 text-accent" />
                        <span className="text-xs font-medium text-white/80">{step.label}</span>
                      </div>
                      {index < arr.length - 1 && <ArrowRight className="w-3.5 h-3.5 text-white/30 hidden sm:block" />}
                    </div>
                  ))}
                </div>
              </AnimatedSection>
            </div>
          </div>
        </div>
      </section>

      {/* Pain Points Section */}
      <PainPointsSection key={`pain-${persona}`} persona={persona} />

      {/* How It Works Section */}
      <div id="how-it-works">
        <AnimatedSection>
          <HowItWorks />
        </AnimatedSection>
      </div>

      {/* Comparison Section */}
      <AnimatedSection>
        <ComparisonSection />
      </AnimatedSection>

      {/* Features Showcase */}
      <div id="features">
        <AnimatedSection>
          <FeaturesShowcase key={`features-${persona}`} persona={persona} />
        </AnimatedSection>
      </div>

      {/* Testimonials Section */}
      <AnimatedSection>
        <TestimonialsSection />
      </AnimatedSection>

      {/* Pricing Preview Section */}
      <div id="pricing">
        <AnimatedSection>
          <PricingPreview />
        </AnimatedSection>
      </div>

      {/* Final CTA Section */}
      <section className="py-20 md:py-28 relative bg-gradient-to-br from-[hsl(222,47%,11%)] via-[hsl(240,30%,14%)] to-[hsl(222,47%,11%)]">
        {/* Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[hsl(277,90%,65%,0.1)] rounded-full blur-[150px]" />
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-[hsl(173,80%,40%,0.1)] rounded-full blur-[120px]" />
        </div>

        <div className="container relative z-10">
          <AnimatedSection>
            <div className="max-w-3xl mx-auto text-center space-y-8">
              {/* Heading */}
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight">
                Your Career Deserves a System,{" "}
                <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                  Not Guesswork
                </span>
              </h2>

              {/* Subheading */}
              <div className="text-lg md:text-xl text-muted-foreground space-y-1">
                <p>Start with a 5-minute values assessment.</p>
                <p>Get a personalized roadmap.</p>
                <p>Build daily momentum.</p>
                <p className="pt-2 font-medium text-foreground">
                  {betaMode
                    ? "Free for all users during beta. Get started today."
                    : "Join 2,000+ professionals building careers with systems, not guesswork."
                  }
                </p>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
                <Button
                  size="lg"
                  onClick={handleCta}
                  className="bg-gradient-to-r from-primary to-accent hover:opacity-90 text-white text-lg px-8 py-6 shadow-lg shadow-primary/25"
                >
                  {ctaText}
                  {betaMode && !hasInviteCode && persona !== "individual" ? <Lock className="ml-2 w-5 h-5" /> : <ArrowRight className="ml-2 w-5 h-5" />}
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="text-lg px-8 py-6 border-white/20 text-white hover:bg-white/10"
                >
                  <Play className="mr-2 w-5 h-5" />
                  Watch 2-Min Demo Video
                </Button>
              </div>

              {/* Trust Elements */}
              <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 pt-4 text-sm text-muted-foreground">
                {(betaMode ? [
                  "Early access to all features",
                  "Free Pro access during beta",
                  "Shape the product with your feedback",
                  "Priority support",
                ] : [
                  "No credit card required",
                  "2,000+ professionals trust MomentumEQ",
                  "7-day Pro trial available",
                  "Cancel anytime",
                ]).map((item) => (
                  <div key={item} className="flex items-center gap-1.5">
                    <CheckCircle2 className="w-4 h-4 text-accent" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[hsl(220,25%,6%)] border-t border-border/30 py-12 md:py-16">
        <div className="container">
          {/* Footer Links */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-8 mb-12">
            {/* Product Column */}
            <div>
              <h4 className="font-display font-semibold text-foreground mb-4">Product</h4>
              <ul className="space-y-3">
                <li>
                  <a
                    href="#how-it-works"
                    className="text-muted-foreground hover:text-foreground transition-colors text-sm"
                  >
                    How It Works
                  </a>
                </li>
                {!betaMode && (
                  <li>
                    <Link
                      to="/pricing"
                      className="text-muted-foreground hover:text-foreground transition-colors text-sm"
                    >
                      Pricing
                    </Link>
                  </li>
                )}
                <li>
                  <a
                    href="#features"
                    className="text-muted-foreground hover:text-foreground transition-colors text-sm"
                  >
                    3E Framework Explained
                  </a>
                </li>
              </ul>
            </div>

            {/* Resources Column */}
            <div>
              <h4 className="font-display font-semibold text-foreground mb-4">Resources</h4>
              <ul className="space-y-3">
                {[
                  { label: "Blog", href: "#" },
                  { label: "Case Studies", href: "#" },
                  { label: "FAQ", href: "#" },
                  { label: "Help Center", href: "#" },
                ].map((link) => (
                  <li key={link.label}>
                    <a
                      href={link.href}
                      className="text-muted-foreground hover:text-foreground transition-colors text-sm"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Company Column */}
            <div className="col-span-2 md:col-span-1">
              <h4 className="font-display font-semibold text-foreground mb-4">Company</h4>
              <ul className="space-y-3">
                {[
                  { label: "About", href: "/about" },
                  { label: "Contact", href: "#" },
                  { label: "Privacy Policy", href: "/privacy" },
                  { label: "Terms of Service", href: "/terms" },
                  { label: "Data Retention", href: "/data-retention" },
                  { label: "DPA", href: "/dpa" },
                ].map((link) => (
                  <li key={link.label}>
                    {link.href.startsWith("/") ? (
                      <Link
                        to={link.href}
                        className="text-muted-foreground hover:text-foreground transition-colors text-sm"
                      >
                        {link.label}
                      </Link>
                    ) : (
                      <a
                        href={link.href}
                        className="text-muted-foreground hover:text-foreground transition-colors text-sm"
                      >
                        {link.label}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Footer Bottom */}
          <div className="pt-8 border-t border-border/30">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              {/* Logo */}
              <BrandLogo size="sm" />

              {/* Social Icons */}
              <div className="flex items-center gap-4">
                <a
                  href="#"
                  className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary/50 transition-all"
                  aria-label="Twitter"
                >
                  <Twitter className="w-5 h-5" />
                </a>
                <a
                  href="#"
                  className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary/50 transition-all"
                  aria-label="LinkedIn"
                >
                  <Linkedin className="w-5 h-5" />
                </a>
                <a
                  href="#"
                  className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary/50 transition-all"
                  aria-label="Email"
                >
                  <Mail className="w-5 h-5" />
                </a>
              </div>

              {/* Copyright */}
              <p className="text-sm text-muted-foreground">© 2026 MomentumEQ. Build your future, daily.</p>
            </div>
          </div>
        </div>
      </footer>

      {/* Waitlist Modal (Individual) */}
      <WaitlistModal open={showWaitlistModal} onOpenChange={setShowWaitlistModal} />

      {/* Pilot Request Modal (Team/Coach) */}
      {persona !== "individual" && (
        <PilotRequestModal
          open={showPilotModal}
          onOpenChange={setShowPilotModal}
          persona={persona}
        />
      )}

      {/* Invite Code Modal */}
      <InviteCodeModal
        open={showInviteCodeModal}
        onOpenChange={setShowInviteCodeModal}
        onWaitlistClick={() => {
          setShowInviteCodeModal(false);
          setShowWaitlistModal(true);
        }}
      />
    </div>
  );
};

export default Landing;
